import "reflect-metadata";

interface BaseConfigAnnotation {
  displayName?: string;
  description?: string;
}

// data to be configured inside the annotation
export interface ConfigOptionAnnotation<T> extends BaseConfigAnnotation {
  options?: Record<string, T>;
  displayType?: KahunConfigurationOptionType;
  isReleased?: boolean;
  isObsolete?: boolean;
  customParams?: Record<string, T>;
}

export interface ConfigObjectAnnotation extends BaseConfigAnnotation {
  backOffice?: boolean;
}

export interface ConfigObjectMapAnnotation extends ConfigObjectAnnotation {
  mapType: new () => any;
}

export enum KahunConfigurationOptionType {
  Int = "int",
  Float = "float",
  String = "string",
  Choice = "choice",
  WPanel = "wpanel",
  WPolicy = "wpolicy",
  WFeature = "wfeature",
  Boolean = "boolean",
  JSON = "json",
  Color = "color",
  MessageTemplate = "messageTemplate",
  Image = "image",
  Text = "text",
  Clinic = "clinic"
}

export type ConfigurationKey = string;
export type ConfigurationPath = string[];
export type ConfigurationDottedKey = string;

// metadata stored for each option
export enum ConfigMetaDataType {
  option = "option",
  subobject = "subobject",
}

export interface ConfigOptionMetadata extends ConfigOptionAnnotation<any> {
  type: ConfigMetaDataType.option;
  storeType: string;
  isArray: boolean;
  isMap: boolean;
  defaultValue: any;
  key: ConfigurationDottedKey;
  path: ConfigurationPath;
}

export interface ConfigSubObjectMetadata extends ConfigObjectAnnotation {
  type: ConfigMetaDataType.subobject;
  objectType: string;
  objectClass: new () => any;
  key: ConfigurationDottedKey;
  path: ConfigurationPath;
  parents?: ConfigSubObjectMetadata[]; // only available when reconstructued via
  isObjectMap?: boolean;
}

// some syntactic sugar to write nicer typescript:
export type ConfigMetaData = ConfigOptionMetadata | ConfigSubObjectMetadata;
export namespace ConfigMetaData {
  export function isOption(c: ConfigMetaData): c is ConfigOptionMetadata {
    return c?.type == ConfigMetaDataType.option;
  }

  export function isSubObject(c: ConfigMetaData): c is ConfigSubObjectMetadata {
    return c?.type == ConfigMetaDataType.subobject;
  }
}

// parents field can only

// a global place to store annotations output as annotiations are fired
export type StoredMetadata = Omit<ConfigMetaData, "path" | "parents">;

var _CONFIG_OPTIONS_METADATA: Map<any, Array<StoredMetadata>> = new Map();

export type ClassOrPrototype = (new () => any) | Object;

export function getMetadataContainerForClass(
  target: ClassOrPrototype,
  readonly = false,
) {
  const name =
    typeof target == "function"
      ? target.name
      : typeof target == "object"
      ? target?.constructor?.name
      : null;
  const constructor =
    typeof target == "function"
      ? target
      : typeof target == "object" && target.constructor
      ? target.constructor
      : null;
  const configs = _CONFIG_OPTIONS_METADATA.get(constructor) || [];
  if (!readonly) {
    if (constructor) {
      _CONFIG_OPTIONS_METADATA.set(constructor, configs);
    } else {
      console.error(
        "getMetadataContainerForClass cant be called with ",
        target,
      );
    }
  }
  return configs;
}

const defaultConfigOptionAnnotation = (key, type) => ({
  displayName: camelCaseToTitle(key),
  description: "",
  displayType:
    type == "number"
      ? KahunConfigurationOptionType.Int
      : type == "boolean"
      ? KahunConfigurationOptionType.Boolean
      : KahunConfigurationOptionType.String,
  isReleased: false,
  key: key,
  storeType: type,
});

// Annotations:
export function configOption(config: ConfigOptionAnnotation<any>) {
  return function (target: any, key: ConfigurationKey) {
    // try to get type
    const t = Reflect.getMetadata("design:type", target, key);
    let storeType = t?.name?.toLowerCase() || "string";
    const isArray = storeType == "array";
    const isMap = storeType == "object";

    if (isArray || isMap) {
      // with an array we dont get the real type, so guess it from displayType if provided
      switch (config.displayType) {
        case KahunConfigurationOptionType.Boolean:
          storeType = "boolean";
          break;
        case KahunConfigurationOptionType.String:
          storeType = "string";
          break;
        case KahunConfigurationOptionType.Int:
        case KahunConfigurationOptionType.Float:
          storeType = "number";
          break;
        default:
          storeType = "string";
      }
    }

    // build the metadata object
    const metadata = {
      type: ConfigMetaDataType.option,
      isArray,
      isMap,
      ...defaultConfigOptionAnnotation(key, storeType),
      ...config,
    } as ConfigOptionMetadata;

    if (metadata.options)
      metadata.displayType = KahunConfigurationOptionType.Choice;

    // get the static default
    if (target?.constructor) {
      const defaultValue = new target.constructor()[metadata.key];
      metadata.defaultValue = defaultValue;
    }

    // store the metadata
    const configs = getMetadataContainerForClass(target);
    configs.push(metadata);
  };
}

export function configSubObject(config: ConfigObjectAnnotation) {
  return function (target: any, key: ConfigurationKey) {
    const t = Reflect.getMetadata("design:type", target, key);
    let objectType = t?.name;
    let objectClass = t;

    // build the metadata object
    const metadata = {
      type: ConfigMetaDataType.subobject,
      displayName: key,
      key,
      objectType,
      objectClass,
      ...config,
    };
    // store the metadata
    const configs = getMetadataContainerForClass(target);
    configs.push(metadata);
  };
}
export function configSubObjectMap(config: ConfigObjectMapAnnotation) {
  return function (target: any, key: ConfigurationKey) {
    const t = Reflect.getMetadata("design:type", target, key);
    if (t?.name !== "Object")
      throw new Error(
        `Mapped type must be a Record<string, ${config.mapType?.name}> type`,
      );

    const objectType = config.mapType?.name;
    const objectClass = config.mapType;

    console.log("map", config.mapType);

    // build the metadata object
    const metadata = {
      type: ConfigMetaDataType.subobject,
      displayName: key,
      key,
      objectType,
      objectClass,
      isObjectMap: true,
      ...config,
    };

    // store the metadata
    const configs = getMetadataContainerForClass(target);
    configs.push(metadata);
  };
}

export function getAllConfigurationMetaData(
  root: ClassOrPrototype,
  pathPrefix: ConfigurationPath = [],
  recursive = true,
  includeSubObjects = false,
): ConfigMetaData[] {
  const configs = getMetadataContainerForClass(root);

  return configs.flatMap((config: ConfigMetaData) => {
    const key = pathToKey([...pathPrefix, config.key]);
    const thisEntry = [{ ...config, key, path: pathPrefix }];
    if (ConfigMetaData.isOption(config)) {
      return thisEntry;
    } else if (ConfigMetaData.isSubObject(config)) {
      if (recursive) {
        return [
          ...(includeSubObjects ? thisEntry : []),
          ...getAllConfigurationMetaData(
            config.objectClass,
            [...pathPrefix, config.key, ...(config.isObjectMap ? ["[]"] : [])],
            true,
            includeSubObjects,
          ),
        ];
      } else return thisEntry;
    } else throw new Error("unknown type");
  });
}

export function pathToKey(path: ConfigurationPath): ConfigurationDottedKey {
  return path?.join(".");
}

export function keyToPath(key: ConfigurationDottedKey) {
  return key?.split(".") || [];
}

function camelCaseToTitle(camelCase: string) {
  return camelCase
    ?.replace(/([A-Z])/g, (match) => ` ${match}`)
    .replace(/^./, (match) => match.toUpperCase())
    .trim();
}
